import React, { useCallback, useRef, useState } from 'react';

import { ImmersiveModalWrapper } from 'src/components/dialogs/ImmersiveModalWrapper';
import { DialogCloseReason } from 'src/components/dialogs/types';

import { AppSettingsModalContent } from './AppSettingsModalContent';
import { AppSettingsModalContext } from './AppSettingsModalContext';
import { AppSettingsModalHeader } from './AppSettingsModalHeader';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';

interface AppSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  moduleType: string;
  moduleId: string;
  appId?: string;
  appName?: string;
}

export const AppSettingsModal = ({
  isOpen,
  onClose,
  moduleType,
  moduleId,
  appId,
  appName = '',
}: AppSettingsModalProps) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isModalFormDirty, setIsModalFormDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const saveFnRef = useRef<(() => Promise<boolean | void>) | null>(null);

  const isModalFormDirtyRef = useRef(isModalFormDirty);
  isModalFormDirtyRef.current = isModalFormDirty;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const attemptClose = useCallback(() => {
    if (isModalFormDirtyRef.current) {
      setShowConfirmDialog(true);
      return;
    }
    setIsModalFormDirty(false);
    onCloseRef.current();
  }, []);

  const handleClose = useCallback(
    (_e: unknown, _reason: DialogCloseReason) => {
      attemptClose();
    },
    [attemptClose],
  );

  const handleConfirmLeave = useCallback(() => {
    setIsModalFormDirty(false);
    setShowConfirmDialog(false);
    onCloseRef.current();
  }, []);

  const handleCancelLeave = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);

  const registerSave = useCallback((fn: () => Promise<boolean | void>) => {
    saveFnRef.current = fn;
  }, []);

  const handleSave = useCallback(async () => {
    if (saveFnRef.current) {
      setIsSaving(true);
      try {
        const shouldClose = await saveFnRef.current();
        setIsModalFormDirty(false);
        if (shouldClose !== false) {
          onCloseRef.current();
        }
      } finally {
        setIsSaving(false);
      }
    }
  }, []);

  const contextValue = React.useMemo(
    () => ({
      onClose: attemptClose,
      onFormDirtyChange: setIsModalFormDirty,
      registerSave,
    }),
    [attemptClose, registerSave],
  );

  if (!isOpen) return null;

  return (
    <>
      <AppSettingsModalContext.Provider value={contextValue}>
        <ImmersiveModalWrapper isOpen={isOpen} onClose={handleClose}>
          <AppSettingsModalHeader
            onClose={attemptClose}
            onSave={() => void handleSave()}
            appName={appName}
            dirty={isModalFormDirty}
            isSaving={isSaving}
          />
          <div
            className="min-h-0 flex-1 overflow-y-auto px-4 sm:px-6 lg:px-10"
            data-testid="settings-scroll-container"
          >
            <div
              className="mx-auto flex min-h-full w-full min-w-0 flex-col max-w-[1396px]"
              data-testid="settings-content-container"
            >
              <AppSettingsModalContent
                moduleType={moduleType}
                moduleId={moduleId}
                appId={appId}
              />
            </div>
          </div>
        </ImmersiveModalWrapper>
      </AppSettingsModalContext.Provider>
      <UnsavedChangesDialog
        isOpen={showConfirmDialog}
        onCancel={handleCancelLeave}
        onLeave={handleConfirmLeave}
      />
    </>
  );
};
